import React, { useState, useEffect, useCallback } from "react";
import {
  fetchCurrentUserInfo,
  fetchProfile,
  fetchTransactions,
  createTransaction,
} from "../script/rpc";

// ── Scan ZIP central-directory entries to identify Office Open XML files ────
// DOCX, XLSX, PPTX are all ZIP files internally. We detect them by looking
// for their unique marker filenames inside the ZIP local-file-header entries.
function detectOfficeFromZip(bytes) {
  // Convert bytes to a searchable string for marker detection
  try {
    let str = "";
    // Only scan first 8KB — the marker files are always near the start
    const limit = Math.min(bytes.length, 8192);
    for (let i = 0; i < limit; i++) str += String.fromCharCode(bytes[i]);

    if (str.includes("word/")) return { mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ext: "docx" };
    if (str.includes("xl/")) return { mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", ext: "xlsx" };
    if (str.includes("ppt/")) return { mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation", ext: "pptx" };
  } catch (_) {}
  return { mime: "application/zip", ext: "zip" };
}

// ── Detect MIME type from file magic bytes ──────────────────────────────────
function detectMime(bytes) {
  const b = bytes;
  // PDF
  if (b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return { mime: "application/pdf", ext: "pdf" };
  // PNG
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return { mime: "image/png", ext: "png" };
  // JPEG
  if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return { mime: "image/jpeg", ext: "jpg" };
  // GIF
  if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return { mime: "image/gif", ext: "gif" };
  // WEBP
  if (b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return { mime: "image/webp", ext: "webp" };
  // Old Office (DOC/XLS/PPT)
  if (b[0] === 0xD0 && b[1] === 0xCF && b[2] === 0x11 && b[3] === 0xE0) return { mime: "application/msword", ext: "doc" };
  // MP4
  if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return { mime: "video/mp4", ext: "mp4" };
  // MP3
  if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return { mime: "audio/mpeg", ext: "mp3" };
  // ZIP — look inside to distinguish DOCX / XLSX / PPTX from a real ZIP
  if (b[0] === 0x50 && b[1] === 0x4B && b[2] === 0x03 && b[3] === 0x04) return detectOfficeFromZip(b);
  return { mime: "application/octet-stream", ext: "bin" };
}

// Messages that indicate the session is broken / user data is unreachable.
const FATAL_AUTH_ERRORS = [
  "could not fetch user information",
  "could not load user partner account",
  "connection failed",
  "network error",
  "invalid username",
  "access denied",
];

function isFatalAuthError(msg = "") {
  const lower = msg.toLowerCase();
  return FATAL_AUTH_ERRORS.some(pattern => lower.includes(pattern));
}

function MiniSpinner() {
  return (
    <span style={{
      display: "inline-block", width: 14, height: 14,
      border: "2px solid currentColor", borderTopColor: "transparent",
      borderRadius: "50%", animation: "spin 0.7s linear infinite",
      verticalAlign: "middle",
    }} />
  );
}

// ── Status badge ────────────────────────────────────────────────────────────
function StateBadge({ state }) {
  const styles = {
    pending: { background: "#fef9c3", color: "#854d0e", border: "1px solid #fde68a" },
    approved: { background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" },
  };
  const labels = { pending: "Pending", approved: "Approved" };
  const s = styles[state] || styles.pending;
  return (
    <span style={{
      ...s, display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
      whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
      {labels[state] || "Pending"}
    </span>
  );
}

export default function TransactionPage({ uid, password, onLogout }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerId, setPartnerId] = useState(null);
  const [partnerName, setPartnerName] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    amount: "", meta: "", attachment: "", attachmentName: "", state: "pending",
  });

  useEffect(() => {
    let alive = true;
    const init = async () => {
      try {
        const userInfo = await fetchCurrentUserInfo(uid, password);
        if (!userInfo?.partner_id) return;
        const pid = userInfo.partner_id[0];
        const pname = userInfo.partner_id[1];
        if (alive) { setPartnerId(pid); setPartnerName(pname); setCurrentUserName(userInfo.name || pname); }
        const profile = await fetchProfile(uid, password, pid);
        if (alive && profile?.mlm_sequence) setNodeId(profile.mlm_sequence);
        const trans = await fetchTransactions(uid, password, pid);
        if (alive) setTransactions(trans);
      } catch (err) {
        if (alive) {
          setError(err.message);
          if (isFatalAuthError(err.message)) {
            setTimeout(() => onLogout && onLogout(), 2000);
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    init();
    return () => { alive = false; };
  }, [uid, password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setFormData(prev => ({
        ...prev,
        attachment: evt.target.result.split(",")[1],
        attachmentName: file.name,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partnerId) return;
    setSubmitting(true); setError(""); setSuccessMsg("");
    const today = new Date().toISOString().split("T")[0];
    const data = {
      partner_id: partnerId,
      node_id: nodeId,
      trans_date: today,
      amount: parseFloat(formData.amount) || 0,
      state: formData.state,
    };
    if (formData.meta) data.meta = formData.meta;
    if (formData.attachment) {
      data.attachment = formData.attachment;
    }
    try {
      await createTransaction(uid, password, data);
      setSuccessMsg("Transaction saved successfully!");
      setFormData({ amount: "", meta: "", attachment: "", attachmentName: "", state: "pending" });
      const trans = await fetchTransactions(uid, password, partnerId);
      setTransactions(trans);
      setTimeout(() => { setShowForm(false); setSuccessMsg(""); }, 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscard = useCallback(() => {
    setShowForm(false);
    setFormData({ amount: "", meta: "", attachment: "", attachmentName: "", state: "pending" });
    setError(""); setSuccessMsg("");
  }, []);

  // ── Download handler: always use original filename if available ────────────
  const handleDownload = useCallback((t) => {
    if (!t.attachment) return;

    try {
      const byteChars = atob(t.attachment);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);

      // Guess MIME type and filename from magic bytes only
      const detected = detectMime(byteArr);
      const mime = detected.mime;
      const downloadName = `transaction_${t.id || t.trans_date}.${detected.ext}`;

      const blob = new Blob([byteArr], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  }, []);

  const todayLabel = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" });

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Inter',system-ui,sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .hdr-btn { display:flex; align-items:center; gap:8px; padding:7px 16px; border-radius:8px; border:1px solid #e2e8f0; background:#fff; color:#475569; font-size:14.5px; font-weight:600; cursor:pointer; transition:all .15s; text-decoration:none; box-shadow:0 1px 2px rgba(0,0,0,.02); }
        .hdr-btn:hover { background:#f8fafc; border-color:#cbd5e1; color:#0f172a; }
        .hdr-danger-btn { display:flex; align-items:center; gap:8px; padding:7px 10px; border-radius:8px; border:1px solid #fecdd3; background:#fff5f5; color:#e11d48; font-size:14.5px; font-weight:600; cursor:pointer; transition:all .15s; }
        .hdr-danger-btn:hover { background:#fee2e2; border-color:#fca5a5; color:#be123c; }
        .tx-input { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:8px; font-size:14px; color:#0f172a; background:#fff; box-sizing:border-box; transition:border .15s; outline:none; font-family:inherit; }
        .tx-input:focus { border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.1); }
        .tx-input[disabled] { background:#f8fafc; color:#94a3b8; cursor:default; border-color:#f1f5f9; }
        .tx-row { cursor:default; transition:background .1s; }
        .tx-row:hover td { background:#f8fafc; }
        .field-label { display:block; font-size:12px; font-weight:600; color:#64748b; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:6px; }
        .dl-btn { display:inline-flex; align-items:center; gap:5px; background:#e0e7ff; color:#4338ca; border:none; padding:4px 10px; border-radius:12px; font-size:12px; font-weight:600; cursor:pointer; transition:background .15s; }
        .dl-btn:hover { background:#c7d2fe; }
      `}</style>

      {/* ══ HEADER ═══════════════════════════════════════════════════════════ */}
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 28px", height: 68, flexShrink: 0,
        background: "#ffffff", borderBottom: "1px solid #e2e8f0",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(99,102,241,.2)" }}>
              <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="4" r="2.5" fill="white" />
                <circle cx="4" cy="15" r="2.5" fill="white" fillOpacity=".8" />
                <circle cx="16" cy="15" r="2.5" fill="white" fillOpacity=".8" />
                <line x1="10" y1="6.5" x2="4" y2="12.5" stroke="white" strokeWidth="1.5" />
                <line x1="10" y1="6.5" x2="16" y2="12.5" stroke="white" strokeWidth="1.5" />
              </svg>
            </div>
            <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" }}>Transactions</span>
          </div>

          <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />

          {!showForm ? (
            <button onClick={() => setShowForm(true)} style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid #6366f1", background: "#6366f1", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              + New
            </button>
          ) : (
            <button onClick={handleDiscard} style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f1f5f9", color: "#475569", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              ← List
            </button>
          )}

          {!loading && !showForm && (
            <>
              <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />
              <span style={{ fontSize: 15, color: "#64748b" }}>
                <strong style={{ color: "#0f172a" }}>{transactions.length}</strong> records
              </span>
            </>
          )}
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <a href="/tree" className="hdr-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            MLM Tree
          </a>
          <a href="/profile" className="hdr-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            Profile
          </a>
          <div style={{ width: 1, height: 20, background: "#e2e8f0", margin: "0 4px" }} />
          {currentUserName && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "14.5px", fontWeight: 600, color: "#1e293b" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff" }}>
                {currentUserName[0].toUpperCase()}
              </div>
              <span>{currentUserName.split(" ")[0]}</span>
            </div>
          )}
          <button onClick={onLogout} className="hdr-danger-btn" title="Sign out">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      {/* ══ CONTENT ══════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, padding: "24px 28px", overflowY: "auto" }}>

        {showForm ? (
          /* ─── FORM VIEW ─────────────────────────────────────────────────── */
          <form id="tx-form" onSubmit={handleSubmit} style={{ maxWidth: 800, margin: "0 auto" }}>

            {/* Messages */}
            {error && <div style={{ background: "#fef2f2", color: "#991b1b", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16, border: "1px solid #fecaca" }}>⚠ {error}</div>}
            {successMsg && <div style={{ background: "#f0fdf4", color: "#166534", padding: "10px 14px", borderRadius: 8, fontSize: 14, marginBottom: 16, border: "1px solid #bbf7d0" }}>✓ {successMsg}</div>}

            {/* Main form card */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,.04)", overflow: "hidden" }}>
              {/* Section header */}
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafafa" }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#0f172a" }}>Transaction Details</h3>
              </div>

              <div style={{ padding: "24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 40px" }}>

                {/* Partner */}
                <div>
                  <label className="field-label">Partner</label>
                  <input className="tx-input" type="text" value={partnerName} disabled />
                </div>

                {/* Node ID */}
                <div>
                  <label className="field-label">Node ID</label>
                  <input className="tx-input" type="text" value={nodeId || "Loading…"} disabled />
                </div>

                {/* Amount */}
                <div>
                  <label className="field-label">Amount <span style={{ color: "#e11d48" }}>*</span></label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6366f1", fontWeight: 700 }}>₹</span>
                    <input
                      className="tx-input" type="number" step="0.01" min="0"
                      name="amount" required value={formData.amount} onChange={handleChange}
                      style={{ paddingLeft: 28 }} placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Transaction Date */}
                <div>
                  <label className="field-label">Transaction Date</label>
                  <input className="tx-input" type="text" value={todayLabel} disabled />
                </div>

                {/* Attachment full-width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="field-label">Attachment</label>
                  <div style={{
                    border: "2px dashed #e2e8f0", borderRadius: 8, padding: "16px 20px",
                    background: formData.attachment ? "#f0fdf4" : "#fafbfc",
                    display: "flex", alignItems: "center", gap: 12, transition: "background .2s",
                  }}>
                    <div style={{ flex: 1 }}>
                      <input type="file" onChange={handleFileChange} style={{ fontSize: 13, color: "#475569", cursor: "pointer", width: "100%" }} />
                    </div>
                    {formData.attachmentName && (
                      <span style={{ fontSize: 12, color: "#166534", fontWeight: 600, whiteSpace: "nowrap" }}>
                        ✓ {formData.attachmentName}
                      </span>
                    )}
                  </div>
                </div>

                {/* Meta / Notes full-width */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label className="field-label">Notes / Meta</label>
                  <textarea
                    className="tx-input" name="meta" value={formData.meta}
                    onChange={handleChange} rows={4}
                    placeholder="Add any notes or additional details about this transaction…"
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", background: "#fafafa", display: "flex", alignItems: "center", gap: 10 }}>
                <button type="submit" disabled={submitting} style={{
                  padding: "8px 20px", borderRadius: 7, border: "none",
                  background: "#6366f1", color: "#fff", fontSize: 14, fontWeight: 600,
                  cursor: submitting ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 7,
                }}>
                  {submitting ? <><MiniSpinner /> Saving…</> : "Save"}
                </button>
                <button type="button" onClick={handleDiscard} style={{
                  padding: "8px 20px", borderRadius: 7, border: "1px solid #e2e8f0",
                  background: "#fff", color: "#475569", fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}>
                  Discard
                </button>
              </div>
            </div>
          </form>

        ) : (
          /* ─── LIST VIEW ─────────────────────────────────────────────────── */
          <>
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px", gap: 14 }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#6366f1", animation: "spin 0.85s linear infinite" }} />
                <p style={{ color: "#64748b", fontSize: 14, fontWeight: 500, margin: 0 }}>Loading transactions…</p>
              </div>
            ) : transactions.length === 0 ? (
              <div style={{ background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 14, padding: "60px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 38, marginBottom: 12 }}>📋</div>
                <p style={{ margin: 0, color: "#64748b", fontSize: 15 }}>No transactions found.</p>
                <button onClick={() => setShowForm(true)} style={{ marginTop: 16, padding: "8px 22px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  + Create First Transaction
                </button>
              </div>
            ) : (
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "12%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e2e8f0", background: "#f8fafc" }}>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569" }}>Date</th>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569" }}>Node ID</th>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569" }}>Partner</th>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569", textAlign: "right" }}>Amount</th>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569" }}>Meta</th>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569", textAlign: "center" }}>Attachment</th>
                      <th style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: "#475569", textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t, idx) => (
                      <tr key={t.id} className="tx-row" style={{ borderBottom: idx < transactions.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                        <td style={{ padding: "12px 16px", fontSize: 13.5, color: "#475569", whiteSpace: "nowrap" }}>{t.trans_date || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13.5, color: "#475569" }}>{t.node_id || "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13.5, color: "#0f172a", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {Array.isArray(t.partner_id) ? t.partner_id[1] : (t.partner_id || partnerName)}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13.5, color: "#0f172a", fontWeight: 600, textAlign: "right", whiteSpace: "nowrap" }}>
                          ₹ {Number(t.amount || 0).toFixed(2)}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t.meta || "—"}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          {t.attachment ? (
                            <button
                              className="dl-btn"
                              onClick={() => handleDownload(t)}
                              title="Download attachment"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              Download
                            </button>
                          ) : (
                            <span style={{ color: "#cbd5e1", fontSize: 13 }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <StateBadge state={t.state || "pending"} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ══ FOOTER ═══════════════════════════════════════════════════════════ */}
      <footer style={{ padding: "8px 28px", background: "#fff", borderTop: "1px solid #e2e8f0", fontSize: 12, textAlign: "center" }}>
        <a href="https://xamta.co/" target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", textDecoration: "none", fontWeight: 500 }}>
          Powered by XAMTA © {new Date().getFullYear()}
        </a>
      </footer>
    </div>
  );
}